import {
  areStoredExperimentsValid,
  clearExperimentsCookie,
  getStoredExperiments,
  setExperimentsCookie,
} from '@core/abTesting/backend/storage'
import type { BackendExperiment } from '@core/abTesting/backend/types'
import { isRawJsonABTestCookiePayload } from '@core/abTesting/cookiePayload'
import { getGlobalConfig } from '@core/globalConfig'
import { getCookie } from '@core/storage'
import { StorageKeyPerformanceEnum } from '@core/storage/types'
import { getGuestId } from '@core/tracking/guestId'

import { logABTestingInfo, logABTestingWarning } from '../logging'

import { getExperimentByAlias } from './alias'
import { fetchExperiments } from './api'
import { trackAbTest } from './tracking'
import { currentPageMatchesUrlScoping } from './urlScoping'

let isInitialized = false
let initPromise: Promise<void> | null = null
let cachedExperiments: BackendExperiment[] | null = null
const initializedListeners = new Set<() => void>()

function markBackendABTestingInitialized(): void {
  if (isInitialized) {
    return
  }

  isInitialized = true
  initializedListeners.forEach((listener) => listener())
}

/**
 * Parses URL parameter overrides for experiments
 * Supports format: fwntests=experiment_type:variant_type,another_type:variant
 */
function parseUrlExperimentOverrides(): BackendExperiment[] | null {
  if (typeof window === 'undefined') {
    return []
  }

  const urlParams = new URLSearchParams(window.location.search)
  const fwntests = urlParams.get('fwntests')

  if (!fwntests) {
    return null
  }

  const overrides: BackendExperiment[] = fwntests
    .split(',')
    .map((experiment) => {
      const [experiment_type, variant_type] = experiment.split(':')
      return {
        experiment_id: `${experiment_type}_${variant_type}_override`,
        base_experiment_type: experiment_type,
        variant_value: variant_type,
        variant_properties: {},
        name: `URL Override: ${experiment_type}=${variant_type}`,
      }
    })
  const experiments = fwntests.split(',')

  experiments.forEach((experiment) => {
    const experiment_override = getExperimentByAlias(experiment)

    if (experiment_override) {
      overrides.push(experiment_override)

      logABTestingInfo(
        `URL parameter override: ${experiment_override.base_experiment_type} ${experiment_override.variant_value}`,
      )
    }
  })

  return overrides
}

/**
 * Returns experiments that are in scope for the current page URL.
 * Filters cached experiments by per-experiment url_conditions rules.
 *
 * Returns null when no experiments apply to the current page — either
 * because none are cached, or because url_conditions exclude this page.
 * The two cases are intentionally collapsed so downstream consumers
 * treat "URL out of scope" the same as "user not in any experiment".
 */
function getExperimentsInScopeForCurrentPage(): BackendExperiment[] | null {
  if (!cachedExperiments || cachedExperiments.length === 0) {
    return null
  }
  const inScope = cachedExperiments.filter((exp) =>
    currentPageMatchesUrlScoping(exp.url_conditions ?? []),
  )
  return inScope.length === 0 ? null : inScope
}

export interface InitializeBackendABTestingParams {
  business_id?: string
  channel_id?: string
}

/**
 * Initializes the backend-driven A/B testing framework
 * This should be called during fwnInit initialization
 */
export function initializeBackendABTesting(
  params?: InitializeBackendABTestingParams,
): Promise<void> {
  if (isInitialized) {
    logABTestingInfo('Backend A/B testing already initialized')
    return Promise.resolve()
  }

  if (initPromise) {
    logABTestingInfo(
      'Backend A/B testing initialization already in progress, reusing promise',
    )
    return initPromise
  }

  initPromise = doInitializeBackendABTesting(params).finally(() => {
    if (!isInitialized) {
      initPromise = null
    }
  })

  return initPromise
}

async function doInitializeBackendABTesting(
  params?: InitializeBackendABTestingParams,
): Promise<void> {
  try {
    const globalConfig = getGlobalConfig()
    const channelId = params?.channel_id
    const businessId = params?.business_id ?? globalConfig.business_id

    // Parse URL parameter overrides
    const urlOverrides = parseUrlExperimentOverrides()
    if (urlOverrides && urlOverrides.length > 0) {
      cachedExperiments = urlOverrides
      logABTestingInfo(
        `Using URL parameter overrides: ${urlOverrides.length} experiments ${JSON.stringify(urlOverrides)}`,
      )
      markBackendABTestingInitialized()
      return
    }

    // Experiments always bucket by business_id and optionally channel_id, per BE
    // Leave the singleton unlocked when it is missing so callers can retry once it is known.
    if (!businessId) {
      logABTestingInfo(
        'No business_id available, waiting to initialize backend A/B testing',
      )
      return
    }

    logABTestingInfo(
      `Initializing backend A/B testing for business_id: ${businessId}, channel_id: ${channelId}`,
    )

    // Check if we have valid cached experiments (including empty [])
    if (areStoredExperimentsValid()) {
      const storedExperiments = getStoredExperiments()
      if (storedExperiments !== null) {
        cachedExperiments = storedExperiments
        logABTestingInfo(
          `Using cached experiments: ${storedExperiments.length} experiments`,
        )
        // Heal legacy raw-JSON fw_abt cookies so Feign clients stop seeing braces
        // without waiting for the 10-minute cache TTL.
        const existingCookie = getCookie(StorageKeyPerformanceEnum.fw_abt)
        if (existingCookie && isRawJsonABTestCookiePayload(existingCookie)) {
          void setExperimentsCookie(storedExperiments)
        }
        markBackendABTestingInitialized()
        return
      }
    }

    // Fetch always includes business_id. ExpPlat also sends channel_id when
    // known; Ava has no channel and sends business_id only.
    const guestId = getGuestId()
    const experiments = await fetchExperiments({
      business_id: businessId,
      guestId,
      ...(channelId ? { channel_id: channelId } : {}),
    })

    logABTestingInfo(
      `Experiments fetched from backend: ${JSON.stringify(experiments)}`,
    )

    // Cache successful responses, including empty arrays (no experiments for
    // this business/guest). Only skip caching on fetch failure (null).
    if (experiments !== null) {
      await setExperimentsCookie(experiments)
      cachedExperiments = experiments

      // Track experiments
      if (experiments.length > 0 && !urlOverrides) {
        trackAbTest(experiments)
      }

      logABTestingInfo(
        `Backend A/B testing initialized with ${experiments.length} experiments`,
      )
    } else {
      logABTestingInfo(
        'No experiments received from backend - fetch failed or unexpected response',
      )
      clearExperimentsCookie()
    }

    markBackendABTestingInitialized()
  } catch (error) {
    logABTestingWarning(`Failed to initialize backend A/B testing: ${error}`)
    isInitialized = false
  }
}

/**
 * Whether backend A/B testing initialization has completed.
 */
export function isBackendABTestingInitialized(): boolean {
  return isInitialized
}

/**
 * Subscribes to backend A/B testing initialization completion.
 */
export function subscribeBackendABTestingInitialized(
  onStoreChange: () => void,
): () => void {
  initializedListeners.add(onStoreChange)
  return () => {
    initializedListeners.delete(onStoreChange)
  }
}

/**
 * Gets the current experiments for this user.
 * Only returns experiments in scope for the current page URL (per-experiment url_conditions).
 */
export function getBackendExperiments(): BackendExperiment[] | null {
  if (!isInitialized) {
    logABTestingInfo(
      'Backend A/B testing not initialized. Call initializeBackendABTesting() first.',
    )
    return null
  }

  return getExperimentsInScopeForCurrentPage() || null
}

/**
 * Gets a specific experiment by ID
 */
export function getExperimentById(
  experimentId: string,
): BackendExperiment | undefined {
  const experiments = getBackendExperiments()
  return experiments?.find((exp) => exp.experiment_id === experimentId)
}

/**
 * Gets a specific experiment by base experiment type
 */
export function getExperimentByType(
  baseExperimentType: string,
): BackendExperiment | undefined {
  const experiments = getBackendExperiments()
  return experiments?.find(
    (exp) => exp.base_experiment_type === baseExperimentType,
  )
}

/**
 * Checks if user is in a specific experiment variant by base experiment type
 */
export function isInExperimentVariant(
  baseExperimentType: string,
  variantType: string,
): boolean {
  const experiments = getBackendExperiments()
  if (experiments?.length === 0) {
    return false
  }

  const experiment = experiments?.find(
    (exp) => exp.base_experiment_type === baseExperimentType,
  )

  return experiment?.variant_value === variantType
}

/**
 * Checks if user is in the hide experiment variant
 */
export function isInEmbedVisibilityHideExperimentVariant(): boolean {
  const experiments = getBackendExperiments()

  if (experiments?.length === 0) {
    return true
  }

  const embedVisibilityExperiment = experiments?.find(
    (exp) => exp.base_experiment_type === 'embed_visibility',
  )
  const embedVisibilityAabbExperiment = experiments?.find(
    (exp) => exp.base_experiment_type === 'embed_visibility_aabb',
  )
  return (
    embedVisibilityExperiment?.variant_value === 'hide' ||
    embedVisibilityAabbExperiment?.variant_value === 'hide1' ||
    embedVisibilityAabbExperiment?.variant_value === 'hide2' ||
    experiments?.length === 0
  )
}

/**
 * Checks if user is in the show experiment variant
 */
export function isInEmbedVisibilityShowExperimentVariant(): boolean {
  if (isInExperimentVariant('embed_visibility', 'show')) {
    return true
  }

  const embedVisibilityAabbExperiment = getExperimentByType(
    'embed_visibility_aabb',
  )
  return (
    embedVisibilityAabbExperiment?.variant_value === 'show1' ||
    embedVisibilityAabbExperiment?.variant_value === 'show2'
  )
}

/**
 * Checks if user is in the firework visibility hide experiment variant
 */
export function isInFireworkVisibilityHideExperimentVariant(): boolean {
  const experiments = getBackendExperiments()

  if (experiments?.length === 0) {
    return true
  }

  const embedVisibilityExperiment = experiments?.find(
    (exp) => exp.base_experiment_type === 'firework_visibility',
  )
  return (
    embedVisibilityExperiment?.variant_value === 'hide' ||
    experiments?.length === 0
  )
}

/**
 * Checks if user is in the show experiment variant
 */
export function isInFireworkVisibilityShowExperimentVariant(): boolean {
  return isInExperimentVariant('firework_visibility', 'show')
}

/**
 * Gets variant_properties for a specific experiment by base experiment type.
 * Returns null if the experiment is not found or has no variant_properties.
 */
export function getVariantPropertiesForExperiment(
  baseExperimentType: string,
): Record<string, string> | null {
  const experiment = getExperimentByType(baseExperimentType)
  return experiment?.variant_properties ?? null
}

/**
 * Gets all experiments formatted for tracking.
 * Only returns experiments in scope for the current page (same as getBackendExperiments).
 * If there are URL overrides, don't return the experiments.
 */
export function getBackendExperimentsForTracking(): BackendExperiment[] | null {
  const experiments = getBackendExperiments()
  // If there are URL overrides, don't return the experiments
  if (experiments?.some((exp) => exp.name.includes('URL Override'))) {
    return null
  }

  return experiments || null
}

/**
 * Forces a refresh of experiments from the backend
 * Useful for testing or when business context changes
 */
export async function refreshExperiments(): Promise<void> {
  logABTestingInfo('Forcing refresh of backend experiments')

  isInitialized = false
  initPromise = null
  cachedExperiments = null
  clearExperimentsCookie()

  await initializeBackendABTesting()
}

/**
 * Resets the backend A/B testing system
 * Useful for testing or cleanup
 */
export function resetBackendABTesting(): void {
  isInitialized = false
  initPromise = null
  cachedExperiments = null
  clearExperimentsCookie()
  logABTestingInfo('Backend A/B testing system reset')
}
